// lib/index.js
import { initCamera } from './camera'
import { initMicrophone, stopMicrophone } from './microphone'
import { initScreenShare } from './screenshare'
import { detectTabSwitch, stopTabSwitchDetection } from './tabswitch'
import { FaceDetection } from './faceDetection'
import { SoundDetection } from './soundDetection'

export class Proctor {
  static videoHandler = null
  static screenHandler = null
  static microphoneStream = null
  static faceDetection = new FaceDetection()
  static soundDetection = new SoundDetection()
  static videoElement = null
  static canvasElement = null
  static isRunning = false

  static async setup(options) {
    if (Proctor.isRunning) {
      console.log('[Proctor] Already running — call stop() before calling setup() again.')
      return
    }

    try {
      await Proctor._setupCamera(options)
      await Proctor._setupMicrophone(options)
      // await Proctor._setupScreenShare(options)
      Proctor._setupTabSwitch(options)

      Proctor.isRunning = true
    } catch (error) {
      console.error('[Proctor] Setup failed — tearing down partial state:', error)
      await Proctor.stop()
      throw error
    }
  }

  static async _setupCamera(options) {
    if (!options.onVideoFrame) return

    if (!options.videoElement || !options.canvasElement) {
      throw new Error(
        '[Proctor] Both videoElement and canvasElement are required when onVideoFrame is provided.'
      )
    }

    const handler = await initCamera()
    Proctor.videoHandler = handler
    options.onVideoFrame(handler.stream)

    await Proctor.faceDetection.loadModel()

    Proctor.videoElement = options.videoElement
    Proctor.canvasElement = options.canvasElement

    await Proctor._waitForVideoReady(Proctor.videoElement)
    Proctor._startFaceDetection(Proctor.videoElement, Proctor.canvasElement, options)
  }

  static async _setupMicrophone(options) {
    if (!options.onAudioStream) return

    const stream = await initMicrophone()
    Proctor.microphoneStream = stream
    options.onAudioStream(stream)

    if (options.onSoundDetect) {
      Proctor.soundDetection.detectSound(Proctor.microphoneStream, options.onSoundDetect)
    }
  }

  static async _setupScreenShare(options) {
    if (!options.onScreenStream) return

    const handler = await initScreenShare()
    Proctor.screenHandler = handler
    options.onScreenStream(handler.stream)
  }

  static _setupTabSwitch(options) {
    if (options.onTabSwitch) {
      detectTabSwitch(options.onTabSwitch)
    }
  }

  // Resolves once the video element has valid dimensions and is playing
  static _waitForVideoReady(videoElement) {
    return new Promise((resolve) => {
      if (videoElement.readyState >= 1 && videoElement.videoWidth > 0) {
        resolve()
        return
      }
      const onMeta = () => {
        videoElement.removeEventListener('loadedmetadata', onMeta)
        videoElement
          .play()
          .catch((err) => console.warn('[Proctor] video.play() failed:', err))
          .finally(resolve)
      }
      videoElement.addEventListener('loadedmetadata', onMeta)
    })
  }

  static _startFaceDetection(videoElement, canvasElement, options) {
    Proctor.faceDetection.detectFaces(
      videoElement,
      canvasElement,
      options.onMultipleFaceDetection,
      options.onvideoDetect,
      options.noFaceDetected,
      options.onLookaway
    )
  }

  static async updateElement(options) {
    if (!options.onVideoFrame) return

    Proctor.faceDetection.stopFaceDetection()
    Proctor.soundDetection.stopDetection()

    if (options.onSoundDetect && Proctor.microphoneStream) {
      Proctor.soundDetection.detectSound(
        Proctor.microphoneStream,
        options.onSoundDetect,
        'from rebinding'
      )
    }

    const { videoElement, canvasElement } = options

    if (!videoElement || !canvasElement) {
      console.warn('[Proctor] updateElement: videoElement and canvasElement are both required.')
      return
    }

    if (Proctor.videoHandler) {
      options.onVideoFrame(Proctor.videoHandler.stream)
    }

    Proctor.videoElement = videoElement
    Proctor.canvasElement = canvasElement

    await Proctor._waitForVideoReady(videoElement)
    Proctor._startFaceDetection(videoElement, canvasElement, options)
  }

  static takeSnapshot(videoElement, canvasElement) {
    const video = videoElement ?? Proctor.videoElement
    void canvasElement // kept for API compatibility; off-screen canvas used instead

    return new Promise((resolve) => {
      if (!video) return resolve(null)

      const offscreen = document.createElement('canvas')
      offscreen.width = video.videoWidth
      offscreen.height = video.videoHeight

      const ctx = offscreen.getContext('2d')
      if (!ctx) return resolve(null)

      ctx.drawImage(video, 0, 0, offscreen.width, offscreen.height)

      offscreen.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], `snapshot-${Date.now()}.png`, { type: 'image/png' }))
        } else {
          resolve(null)
        }
      }, 'image/png')
    })
  }

  static async stop() {
    Proctor.stopCamera()
    Proctor.stopMicrophone()
    Proctor.stopScreenShare()
    Proctor.stopTabSwitchDetection()
    Proctor.isRunning = false
    console.log('[Proctor] Fully stopped.')
  }

  static stopCamera() {
    if (Proctor.videoHandler) {
      Proctor.videoHandler.stop()
      Proctor.videoHandler = null
    }
    Proctor.faceDetection.stopFaceDetection()
  }

  static stopMicrophone() {
    if (Proctor.microphoneStream) {
      stopMicrophone(Proctor.microphoneStream)
      Proctor.microphoneStream = null
    }
    Proctor.soundDetection.stopDetection()
  }

  static stopScreenShare() {
    if (Proctor.screenHandler) {
      Proctor.screenHandler.stop()
      Proctor.screenHandler = null
    }
  }

  static stopTabSwitchDetection() {
    stopTabSwitchDetection()
  }
}
